import {
  recordClickthrough,
  recordDismissal,
  recordShow,
  showIfAppropriate,
} from 'js/utils/dismissibles';

export type FeedbackClickthrough = { rating: number; has_comment: boolean };

export interface FeedbackPersistence {
  eligible: () => Promise<boolean>;
  recordShow: () => void;
  recordDismissal: () => void;
  recordClickthrough: (data: FeedbackClickthrough) => void;
}

export const serverFeedbackPersistence = (
  name: Parameters<typeof showIfAppropriate>[0],
): FeedbackPersistence => ({
  eligible: async () => {
    let eligible = false;
    await showIfAppropriate(name, () => {
      eligible = true;
    });
    return eligible;
  },
  recordShow: () => {
    recordShow(name);
  },
  recordDismissal: () => {
    recordDismissal(name);
  },
  recordClickthrough: (data) => recordClickthrough(name, data),
});

type LocalHistory = { shows: number; lastShownAt: number; dismissals: number };

// Keep preview defaults aligned with the server-side feedback limits.
export const DEFAULT_FEEDBACK_LIMITS = {
  maxShows: 3,
  maxDismissals: 1,
  minDelayMs: 24 * 60 * 60 * 1000,
};

export const localFeedbackPersistence = (
  key: string,
  limits = DEFAULT_FEEDBACK_LIMITS,
): FeedbackPersistence => {
  let history: LocalHistory = { shows: 0, lastShownAt: 0, dismissals: 0 };
  const read = (): LocalHistory => {
    try {
      const stored: LocalHistory | null = JSON.parse(
        window.localStorage.getItem(key) ?? 'null',
      );
      if (
        stored &&
        [stored.shows, stored.lastShownAt, stored.dismissals].every(
          (value) => Number.isFinite(value) && value >= 0,
        )
      )
        history = stored;
    } catch {
      // Blocked storage falls back to this page's in-memory history.
    }
    return history;
  };
  const update = (next: LocalHistory): void => {
    history = next;
    try {
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Keep the in-memory update when storage is blocked.
    }
  };
  return {
    eligible: async () => {
      const state = read();
      return (
        state.shows < limits.maxShows &&
        state.dismissals < limits.maxDismissals &&
        (state.shows === 0 ||
          Date.now() - state.lastShownAt >= limits.minDelayMs)
      );
    },
    recordShow: () => {
      const state = read();
      update({ ...state, shows: state.shows + 1, lastShownAt: Date.now() });
    },
    recordDismissal: () => {
      const state = read();
      update({ ...state, dismissals: state.dismissals + 1 });
    },
    recordClickthrough: () => undefined,
  };
};
